import AnchorIcon from "@mui/icons-material/Anchor";
import * as React from "react";

import type { HoverAction } from "../HoverActionToolbar/hoverAction";

import { formatAnchorTime } from "./formatAnchorTime";
import { createAnchor } from "./timeAnchor";
import { TimestampFormat } from "./timestampFormat";
import { useTimestampFormatActions } from "./TimestampFormatContext";

/**
 * The "Set as t=0" toolbar action for a rendered instant, so a reader can
 * anchor the thread's relative display to the moment they just read about
 * rather than hunting for a 19-digit number to paste into settings.
 *
 * The instant they picked stays t=0 until they change or clear it from the
 * time-format settings — Relative counts from a fixed instant and nothing
 * moves it underneath them.
 *
 * `undefined` when there is no instant to anchor on, or outside a
 * {@link TimestampFormatProvider} — dataset-page AI summaries render the same
 * timestamp renderers with nowhere to put an anchor. Callers hand the result
 * straight to {@link hoverActions}, which drops it in either case.
 */
export function useSetAnchorAction(
  ns: bigint | undefined,
): HoverAction | undefined {
  const actions = useTimestampFormatActions();

  return React.useMemo(() => {
    if (ns === undefined || actions === undefined) {
      return undefined;
    }
    return {
      key: "set-anchor",
      icon: <AnchorIcon />,
      tooltip: "Set as t=0",
      onClick: () => {
        actions.setAnchor(createAnchor(ns, formatAnchorTime(ns)));
        // Anchoring is only ever asked for in order to read relative times, so
        // the format switch is the intent rather than a side effect: without it
        // the click would appear to do nothing.
        actions.setFormat(TimestampFormat.Relative);
      },
    };
  }, [ns, actions]);
}
